import React, { useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import { Trans } from '@lingui/react'

import useOnboardingForm from './useAdminOnboardingForm'
import ModalInviteUsers from './AdminOnboardingModalInviteUsers'
import ModalLinkO365 from './AdminOnboardingModalLinkO365'
import ModalWelcome from './AdminOnboardingModalWelcome'
import ModalGetStarted from './AdminOnboardingModalGetStarted'

import PageNotFound from '../../../layout/PageNotFound'
import PageWithState from '../../../layout/PageWithState'
import useAppState from '../../../hooks/useAppState'
import useNotifications from '../../../hooks/useNotifications'
import useAppSecrets from '../../../hooks/useAppSecrets'
import useTracking from '../../../hooks/useTracking'
import useTenantSettings from '../../../hooks/useTenantSettings'

import { PRODUCT_SLUG_PLUGIN } from '../../../../util/constants'
import updateSubscription from '../../../../api/updateSubscription'
import updateAccountStateCompletion from '../../../../api/updateAccountStateCompletion'
import createCoworkers from '../../../../api/createCoworkers'
import completeOnboarding from '../../../../api/completeOnboarding'
import { linkOffice365Account } from '../../../../util/office365'
import * as routes from '../../../../routes/index'

const TOTAL_STEPS = 5

const AdminOnboardingModalWithState = ({ ...props }) => {
    const navigate = useNavigate()
    const { step } = useParams()
    const location = useLocation()
    const { state : authorizReponse } = location
    const { refresh } = useAppState()
    const { currentPlan } = useTenantSettings()
    const { trackError } = useTracking()
    const { o365DrivesClientId, o365MailsAndCalendarsClientId } = useAppSecrets()
    const apolloClient = useApolloClient()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { dispatchError } = useNotifications()
    const formState = useOnboardingForm()
    const { values } = formState
    const currentStep = Number(step)
    const nextStep = (stepSize = 1) =>
        navigate(routes.adminOnboarding(currentStep + stepSize))
    const [disclaimerChecked, checkDisclaimer] = useState(false)
    switch (currentStep) {
        case 1:
            return (
                <PageWithState {...props}>
                    <ModalWelcome
                        showNextButton
                        totalSteps={TOTAL_STEPS}
                        isSubmitting={isSubmitting}
                        onNext={() => {
                            if (
                                currentPlan.product.slug === PRODUCT_SLUG_PLUGIN
                            ) {
                                nextStep(2)
                            } else {
                                nextStep()
                            }
                        }}
                        {...props}
                    />
                </PageWithState>
            )
        case 2:
            return (
                <PageWithState {...props}>
                    <ModalInviteUsers
                        showNextButton={values.emailAddresses.length > 0}
                        showSkipButton={values.emailAddresses.length === 0}
                        totalSteps={TOTAL_STEPS}
                        isSubmitting={isSubmitting}
                        formState={formState}
                        onNext={async () => {
                            setIsSubmitting(true)
                            const coworkersInput = formState.valuesToInput()
                            const quantity =
                                coworkersInput.emailAddresses.length + 1
                            const subscriptionInput = {
                                planId: currentPlan.id,
                                quantity,
                            }
                            try {
                                await updateSubscription(
                                    apolloClient,
                                    subscriptionInput,
                                )
                                await Promise.all([
                                    createCoworkers(
                                        apolloClient,
                                        coworkersInput.emailAddresses,
                                    ),
                                    updateAccountStateCompletion(apolloClient, {
                                        hasInvitedUsers: true,
                                    }),
                                ])
                                nextStep()
                            } catch (e) {
                                trackError(e)
                                dispatchError({
                                    title: <Trans id="Something went wrong!" />,
                                    content: e.message,
                                })
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}
                        onSkip={async () => {
                            setIsSubmitting(true)
                            try {
                                await updateAccountStateCompletion(
                                    apolloClient,
                                    { hasInvitedUsers: true },
                                )
                                nextStep()
                            } catch (e) {
                                trackError(e)
                                dispatchError({
                                    title: <Trans id="Something went wrong!" />,
                                    content: e.message,
                                })
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}
                        {...props}
                    />
                </PageWithState>
            )
        case 3: // link to Mail&Calendar
            return (
                <PageWithState {...props}>
                    <ModalLinkO365
                        authorizReponse={authorizReponse}
                        linkTo="mails_calendars"
                        showNextButton
                        totalSteps={TOTAL_STEPS}
                        isSubmitting={isSubmitting}
                        onNext={() => nextStep()}
                        onLinkOffice365Account={async () => {
                            setIsSubmitting(true)
                            try {
				            await linkOffice365Account(
                                o365MailsAndCalendarsClientId,
                                '',
                                '/admin-onboarding',
                                'mails_calendars',
                            )
                                await updateAccountStateCompletion(
                                    apolloClient,
                                    {  hasLinkedO365: true
                                     },
                                )
                            } catch (e) {
                                trackError(e)
                                dispatchError({
                                    title: <Trans id="Something went wrong!" />,
                                    content: e.message,
                                })
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}
                        {...props}
                    />
                </PageWithState>
            )
            case 4: // link to Drives
                return (
                    <PageWithState {...props}>
                        <ModalLinkO365
                            authorizReponse={authorizReponse}
                            linkTo="drives"
                            showSkipButton
                            totalSteps={TOTAL_STEPS}
                            isSubmitting={isSubmitting}
                            onSkip={() => nextStep()}
                            onLinkOffice365Account={async () => {
                                setIsSubmitting(true)
                                try {
				                    await linkOffice365Account(
                                    o365DrivesClientId, 
                                    '', 
                                    '/admin-onboarding',
                                    'drives',
                                    )
                                    await updateAccountStateCompletion(
                                        apolloClient,
                                        { hasLinkedO365: true },
                                    )
                                } catch (e) {
                                    trackError(e)
                                    dispatchError({
                                        title: <Trans id="Something went wrong!" />,
                                        content: e.message,
                                    })
                                } finally {
                                    setIsSubmitting(false)
                                }
                            }}
                            {...props}
                        />
                    </PageWithState>
                )    
        case 5:
            return (
                <PageWithState {...props}>
                    <ModalGetStarted
                        disclaimerChecked={disclaimerChecked}
                        handleCheckDisclaimer={checkDisclaimer}
                        isSubmitting={isSubmitting}
                        onGetStarted={async () => {
                            setIsSubmitting(true)
                            try {
                                await completeOnboarding(apolloClient)
                                navigate(routes.home())
                                refresh()
                            } catch (e) {
                                trackError(e)
                                dispatchError({
                                    title: <Trans id="Something went wrong!" />,
                                    content: e.message,
                                })
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}
                        {...props}
                    />
                </PageWithState>
            )
        default:
            return <PageNotFound />
    }
}

export default AdminOnboardingModalWithState
